import type { State } from '../State';
import features from '../ruleset/features';
import { getAttributeValue } from '../pet/pet.getters';
import { getJSTDateTime } from '../utils/time';
import {
  JANKEN_CHALLENGE_EXPIRY_MS,
  JANKEN_MAX_DAILY_REWARDS,
  jankenSchedule,
  JANKEN_UNLOCK_LEVEL,
} from './janken.ruleset';
import { jankenMapKey, parseJankenChallengeTimestamp } from './janken.utils';

/**
 * Feature availability check. Requires the petJanken feature flag (AB test,
 * feature flag, non-guest) AND match2 level >= `JANKEN_UNLOCK_LEVEL`.
 *
 * @example
 * if (jankenIsAvailable(state)) {
 *   // show janken UI
 * }
 */
export function jankenIsAvailable(state: State): boolean {
  if (!features(state).petJanken) return false;
  if (state.match2.level < JANKEN_UNLOCK_LEVEL) return false;
  return true;
}

/**
 * Check if the user has seen the janken tutorial (info popup).
 * Returns false for first-time users who haven't opened janken yet.
 */
export function jankenHasSeenTutorial(state: State): boolean {
  return !!state.janken?.tutorialSeenAt;
}

/**
 * Check if the janken event is currently active (feature available + schedule running).
 */
export function jankenIsEventActive(state: State, now: number): boolean {
  return jankenIsAvailable(state) && jankenSchedule.hasActiveEvent(now);
}

/**
 * Get the current janken event with remaining time, or undefined if inactive.
 */
export function jankenGetCurrentEvent(
  state: State,
  now: number,
):
  | {
      remainingTime: number;
      id: string;
      start: number;
      end: number;
      metadata?: undefined;
    }
  | undefined {
  if (!jankenIsEventActive(state, now)) return undefined;
  const event = jankenSchedule.getCurrentEvent(now);
  if (!event) return undefined;
  return { ...event, remainingTime: Math.max(0, event.end - now) };
}

/**
 * Check care gate: both stomach AND love must be above 0 to play janken.
 *
 * @example
 * if (!jankenGatePasses(state, now)) {
 *   // enter care sub-sequence — pet needs feeding or attention
 * }
 */
export function jankenGatePasses(state: State, now: number): boolean {
  return (
    getAttributeValue(state, 'stomach', now) > 0 &&
    getAttributeValue(state, 'love', now) > 0
  );
}

/**
 * Diagnose which meters are failing the janken care gate.
 * Returns null when both meters are above 0 (gate passes).
 *
 * Used by the care sub-sequence to determine which care type to offer
 * (food for stomach, toy for love, snack for both).
 *
 * @example
 * const failure = jankenGateFailure(state, now);
 * if (failure?.stomachZero && failure?.loveZero) {
 *   // offer snack (feeds both meters)
 * } else if (failure?.stomachZero) {
 *   // offer food
 * } else if (failure?.loveZero) {
 *   // offer toy
 * }
 */
export function jankenGateFailure(
  state: State,
  now: number,
): { stomachZero: boolean; loveZero: boolean } | null {
  const stomach = getAttributeValue(state, 'stomach', now);
  const love = getAttributeValue(state, 'love', now);
  if (stomach > 0 && love > 0) return null;
  return { stomachZero: stomach <= 0, loveZero: love <= 0 };
}

/**
 * Get the current daily reward count.
 * Returns 0 if janken state is not initialized.
 *
 * @example
 * const earned = getJankenDailyRewards(state); // 0–5
 */
export function getJankenDailyRewards(state: State): number {
  return state.janken?.dailyRewards ?? 0;
}

/**
 * Check if a reward can be granted for this friend.
 * Combines two checks: daily limit (JANKEN_MAX_DAILY_REWARDS) AND per-friend-per-day de-duplication.
 *
 * If dailyStamp is missing (first-time user), treats it as a fresh day with 0 rewards.
 * This is safe because the action layer calls resetJankenDaily before granting rewards,
 * which sets dailyStamp. Until that happens, the missing dailyStamp causes
 * isDailyStampToday to return false, so dailyRewards falls back to 0.
 *
 * @example
 * if (result.receiver === 'win' && canGrantJankenReward(state, friendId, now)) {
 *   grantMatch2Rewards(state, [reward], ...);
 *   recordJankenReward(state, friendId, now);
 * }
 */
export function canGrantJankenReward(
  state: State,
  friendId: string,
  now: number,
): boolean {
  const dailyRewards = isDailyStampToday(state, now)
    ? getJankenDailyRewards(state)
    : 0;
  if (dailyRewards >= JANKEN_MAX_DAILY_REWARDS) return false;
  return !isResolvedFriendToday(state, friendId, now);
}

/**
 * Check if the stored dailyStamp is on the same JST calendar day as `now`.
 * Returns false if janken state or dailyStamp is not initialized.
 *
 * @example
 * const dailyRewards = isDailyStampToday(state, now)
 *   ? getJankenDailyRewards(state) // same day — use stored count
 *   : 0;                           // new day — treat as fresh
 */
function isDailyStampToday(state: State, now: number): boolean {
  const stamp = state.janken?.dailyStamp;
  if (stamp == null) return false;
  return getJSTDateTime(stamp).hasSame(getJSTDateTime(now), 'day');
}

/**
 * Check if the current user already earned a reward for playing with this friend
 * today (JST calendar day). Compares the stored timestamp's JST day against `now`'s JST day.
 *
 * @example
 * if (isResolvedFriendToday(state, friendId, now)) {
 *   // skip reward — already played with this friend today
 * }
 */
export function isResolvedFriendToday(
  state: State,
  friendId: string,
  now: number,
): boolean {
  const stamp = state.janken?.resolvedFriends?.[friendId];
  if (stamp == null) return false;
  return getJSTDateTime(stamp).hasSame(getJSTDateTime(now), 'day');
}

/**
 * Replay guard: check if this challengeId+senderId pair was already resolved.
 * Prevents duplicate OA notifications and reward grants on re-tap.
 *
 * @example
 * if (isResolvedChallenge(state, challengeId, senderId)) {
 *   return; // duplicate tap — outcome still shown client-side (deterministic)
 * }
 */
export function isResolvedChallenge(
  state: State,
  challengeId: string,
  senderId: string,
): boolean {
  return !!state.janken?.resolvedChallenges?.[
    jankenMapKey(challengeId, senderId)
  ];
}

/**
 * Validate that a challengeId has the expected `jnk_{timestamp}_{random}` format.
 * Use this before calling `isJankenChallengeExpired` in the action layer to
 * distinguish malformed IDs from genuinely expired challenges and log accordingly.
 *
 * @example
 * if (!isValidJankenChallengeId(challengeId)) {
 *   logError('Malformed janken challengeId', { challengeId });
 *   return;
 * }
 */
export function isValidJankenChallengeId(challengeId: string): boolean {
  return parseJankenChallengeTimestamp(challengeId) !== null;
}

/**
 * Check if a challenge is expired (> 24h from the creation timestamp embedded in the challengeId).
 * Malformed challenge IDs are treated as expired as a safe fallback.
 *
 * Callers should validate the challengeId format first via `isValidJankenChallengeId`
 * and log a warning for malformed IDs before reaching this point.
 *
 * @example
 * if (!isValidJankenChallengeId(challengeId)) {
 *   logError('Malformed janken challengeId', { challengeId });
 *   return;
 * }
 * if (isJankenChallengeExpired(challengeId, now)) {
 *   // challenge has expired
 * }
 */
export function isJankenChallengeExpired(
  challengeId: string,
  now: number,
): boolean {
  const createdAt = parseJankenChallengeTimestamp(challengeId);
  if (!createdAt) return true;
  return now - createdAt > JANKEN_CHALLENGE_EXPIRY_MS;
}

/**
 * Check if there are any non-expired pending results.
 *
 * @example
 * if (jankenHasPendingResults(state, now)) {
 *   // show HUD notification badge
 * }
 */
export function jankenHasPendingResults(state: State, now: number): boolean {
  if (!state.janken?.pendingResults) return false;
  return Object.values(state.janken.pendingResults).some(
    (p) => !isJankenChallengeExpired(p.challengeId, now),
  );
}

/**
 * Get all pending results sorted by createdAt (oldest first).
 *
 * @example
 * for (const pending of getJankenPendingResults(state)) {
 *   await claimAndShow(pending);
 * }
 */
export function getJankenPendingResults(state: State): Array<{
  key: string;
  challengeId: string;
  receiverId: string;
  createdAt: number;
  opponentSpecies?: string;
  opponentDisplayName?: string;
}> {
  if (!state.janken?.pendingResults) return [];
  return Object.entries(state.janken.pendingResults)
    .sort((a, b) => a[1].createdAt - b[1].createdAt)
    .map(([key, data]) => ({ ...data, key }));
}

/**
 * Get a specific pending result by key.
 *
 * @example
 * const pending = getJankenPendingResultById(state, 'jnk_1708300000000_482917365_U9e8...');
 * // => { challengeId: 'jnk_1708300000000_482917365', receiverId: 'U9e8...', createdAt: 1708300000000 }
 */
export function getJankenPendingResultById(
  state: State,
  key: string,
): {
  challengeId: string;
  receiverId: string;
  createdAt: number;
  opponentSpecies?: string;
  opponentDisplayName?: string;
} | null {
  return state.janken?.pendingResults?.[key] ?? null;
}

/**
 * Check if a pending result already exists for this challengeId+receiverId pair.
 * Used for dedup in the postMessage handler.
 *
 * @example
 * if (isExistingJankenResult(state, challengeId, receiverId)) {
 *   return; // already have a pending result for this pair
 * }
 */
export function isExistingJankenResult(
  state: State,
  challengeId: string,
  receiverId: string,
): boolean {
  if (!state.janken?.pendingResults) return false;
  return jankenMapKey(challengeId, receiverId) in state.janken.pendingResults;
}
